/**
 * @fileoverview Avatar Companion Phase 3 - Enhanced Personality Inference Types
 * @module shared/types/PersonalityInferenceV3
 * @version 3.0.0
 * @author Telegamez Avatar System
 * 
 * @description
 * Advanced TypeScript type definitions for Phase 3 enhanced personality inference system.
 * Includes zero-shot AI capabilities, multimodal analysis, real-time streaming,
 * adaptive companion behavior, and privacy-compliant processing.
 * 
 * @dependencies
 * - zod: Runtime schema validation
 * - ./AvatarCompanionPhase1: Base personality types
 * - Vercel AI SDK: Streaming AI inference types
 * 
 * @features
 * - Zero-shot personality detection with confidence scoring
 * - Multimodal analysis (voice, text, behavioral patterns)
 * - Real-time streaming personality updates
 * - Adaptive companion behavior engine
 * - Cross-modal validation and uncertainty handling
 * - Privacy-compliant data structures with GDPR/CCPA support
 * - Performance-optimized caching with confidence-based TTL
 */

import { z } from 'zod';
import { 
  OceanPersonalityValues,
  SpeakerPersonalityState,
  CommunicationStyle,
  EnergyLevel,
  FormalityLevel,
  HumorUsage,
  ResponseStyle,
  MotivationalApproach
} from './AvatarCompanionPhase1';

// =============================================================================
// PRIVACY AND CONFIGURATION TYPES
// =============================================================================

/**
 * Privacy levels for personality data processing
 */
export enum PrivacyLevel {
  MINIMAL = 'minimal',
  STANDARD = 'standard', 
  HIGH = 'high'
}

// =============================================================================
// ZERO-SHOT PERSONALITY INFERENCE TYPES
// =============================================================================

/**
 * Advanced personality score with confidence and uncertainty tracking
 */
export interface PersonalityScore {
  /** Normalized score (0-100) */
  value: number;
  
  /** Confidence in this assessment (0-1) */
  confidence: number;
  
  /** Supporting evidence from analysis */
  evidence: string[];
  
  /** Uncertainty/ambiguity level (0-1) */
  uncertainty: number;
  
  /** Temporal stability of this trait (0-1) */
  temporalStability: number;
  
  /** Data sources contributing to this score */
  sources: Array<{
    type: 'voice' | 'text' | 'behavioral' | 'contextual';
    weight: number; // 0-1 contribution to final score
    timestamp: Date;
  }>;
}

/**
 * Zero-shot personality inference with advanced capabilities
 */
export interface ZeroShotPersonalityInference {
  /** Core OCEAN Traits with confidence scoring */
  oceanTraits: {
    openness: PersonalityScore;
    conscientiousness: PersonalityScore;
    extraversion: PersonalityScore;
    agreeableness: PersonalityScore;
    neuroticism: PersonalityScore;
  };
  
  /** Communication pattern analysis */
  communicationPatterns: {
    formality: PersonalityScore;
    verbosity: PersonalityScore;
    emotionalExpression: PersonalityScore;
    technicalLanguage: PersonalityScore;
    humor: PersonalityScore;
    empathy: PersonalityScore;
  };
  
  /** Cognitive style analysis */
  cognitiveStyle: {
    analyticalThinking: PersonalityScore;
    creativityLevel: PersonalityScore;
    detailOrientation: PersonalityScore;
    abstractThinking: PersonalityScore;
    problemSolvingApproach: PersonalityScore;
  };
  
  /** Social interaction preferences */
  socialStyle: {
    collaborationPreference: PersonalityScore;
    leadershipTendency: PersonalityScore;
    conflictResolution: PersonalityScore;
    supportiveness: PersonalityScore;
    assertiveness: PersonalityScore;
  };
  
  /** Learning and motivation patterns */
  motivationalProfile: {
    achievementOrientation: PersonalityScore;
    curiosityLevel: PersonalityScore;
    persistenceLevel: PersonalityScore;
    feedbackReceptivity: PersonalityScore;
    riskTolerance: PersonalityScore;
  };
}

/**
 * Enhanced personality context with multimodal inputs
 */
export interface EnhancedPersonalityContext {
  /** Basic identification */
  userId: string;
  sessionId: string;
  roomId: string;
  
  /** Voice analysis data */
  voicePatterns?: {
    speechRate: number; // words per minute
    pausePatterns: number[]; // pause durations in seconds
    tonalVariation: number; // 0-1 voice tone variation
    stressIndicators: number; // 0-1 vocal stress level
    confidenceMarkers: number; // 0-1 vocal confidence level
    emotionalIntensity: number; // 0-1 emotional expression intensity
    articulationClarity: number; // 0-1 speech clarity
    prosodyPatterns: {
      pitch: { mean: number; variance: number; range: number };
      volume: { mean: number; variance: number; range: number };
      tempo: { mean: number; variance: number; range: number };
    };
  };
  
  /** Behavioral pattern analysis */
  behavioralPatterns?: {
    messageFrequency: number; // messages per minute
    responseLatency: number[]; // response times in seconds
    topicInitiation: number; // 0-1 tendency to start new topics
    questionAsking: number; // questions per message
    followUpEngagement: number; // 0-1 engagement with responses
    conversationMaintenance: number; // 0-1 effort to keep conversation going
    turnTaking: {
      interruptions: number; // interruption count
      waitTime: number; // average wait time before speaking
      turnLength: number; // average turn duration
    };
  };
  
  /** Contextual factors */
  contextualFactors?: {
    timeOfDay: string; // for circadian personality variation
    conversationLength: number; // minutes
    participantDynamics: {
      groupSize: number;
      familiarityLevel: number; // 0-1 how well participants know each other
      powerDynamics: number; // 0-1 formal/informal hierarchy
    };
    environmentalFactors: {
      noiseLevel: number; // 0-1 background noise
      connectivity: number; // 0-1 connection quality
      deviceType: 'mobile' | 'desktop' | 'tablet';
    };
  };
  
  /** Historical personality data */
  personalityHistory?: Array<{
    timestamp: Date;
    snapshot: Partial<ZeroShotPersonalityInference>;
    contextSummary: string;
    driftIndicators: number[]; // personality trait drift over time
  }>;
}

// =============================================================================
// ADAPTIVE PARAMETER TYPES
// =============================================================================

/**
 * Adaptive parameter with learning capabilities
 */
export interface AdaptiveParameter {
  /** Default parameter value */
  baseValue: number;
  
  /** Current adapted value */
  adaptedValue: number;
  
  /** Min/max adaptation bounds */
  adaptationRange: [number, number];
  
  /** How much to adapt based on inference (0-1) */
  adaptationFactor: number;
  
  /** Adaptation history for learning */
  adaptationHistory: Array<{
    timestamp: Date;
    value: number;
    trigger: string; // What caused this adaptation
    performance: number; // 0-1 how well this worked
  }>;
  
  /** Minimum confidence required for adaptation */
  confidenceThreshold: number;
  
  /** Seconds before next adaptation allowed */
  stabilityPeriod: number;
}

/**
 * Enhanced style profile with adaptive parameters
 */
export interface EnhancedStyleProfile {
  /** Style profile ID */
  id: string;
  
  /** Associated companion ID */
  companionId: string;
  
  /** Adaptive LLM parameters */
  adaptiveLLMParams: {
    temperature: AdaptiveParameter;
    topP: AdaptiveParameter;
    topK: AdaptiveParameter;
    frequencyPenalty: AdaptiveParameter;
    presencePenalty: AdaptiveParameter;
    repetitionPenalty: AdaptiveParameter;
    maxTokens: AdaptiveParameter;
  };
  
  /** Communication style adaptations */
  communicationAdaptations: {
    formalityAdjustment: AdaptiveParameter;
    verbosityModulation: AdaptiveParameter;
    technicalLanguageLevel: AdaptiveParameter;
    humorIntegration: AdaptiveParameter;
    empathyExpression: AdaptiveParameter;
    encouragementLevel: AdaptiveParameter;
    challengeIntensity: AdaptiveParameter;
  };
  
  /** Content structuring preferences */
  contentStructuring: {
    informationDensity: AdaptiveParameter;
    exampleUsage: AdaptiveParameter;
    analogyFrequency: AdaptiveParameter;
    storytellingElement: AdaptiveParameter;
    visualDescriptions: AdaptiveParameter;
    stepByStepBreakdown: AdaptiveParameter;
  };
  
  /** Interaction patterns */
  interactionPatterns: {
    questioningStyle: AdaptiveParameter;
    feedbackSensitivity: AdaptiveParameter;
    conversationPacing: AdaptiveParameter;
    topicTransitions: AdaptiveParameter;
    conflictAvoidance: AdaptiveParameter;
    supportiveness: AdaptiveParameter;
  };
  
  /** Personalization strategies */
  personalizationStrategies: {
    learningStyleAlignment: AdaptiveParameter;
    motivationalAlignment: AdaptiveParameter;
    cognitiveLoadManagement: AdaptiveParameter;
    attentionSpanConsideration: AdaptiveParameter;
    preferenceAdaptation: AdaptiveParameter;
  };
  
  /** Metadata */
  createdAt: Date;
  updatedAt: Date;
}

// =============================================================================
// MULTIMODAL ANALYSIS TYPES
// =============================================================================

/**
 * Voice pattern analysis for personality inference
 */
export interface VoicePatternAnalysis {
  /** Core voice metrics */
  acousticFeatures: {
    fundamentalFrequency: {
      mean: number;
      variance: number;
      range: number;
      contour: number[]; // F0 contour over time
    };
    intensity: {
      mean: number;
      variance: number;
      dynamicRange: number;
      energyDistribution: number[];
    };
    spectralFeatures: {
      spectralCentroid: number;
      spectralRolloff: number;
      mfccCoefficients: number[]; // Mel-frequency cepstral coefficients
      harmonicToNoiseRatio: number;
    };
    temporalFeatures: {
      speechRate: number;
      articulationRate: number;
      pauseDuration: number[];
      voiceOnsetTime: number[];
    };
  };
  
  /** Emotional voice indicators */
  emotionalIndicators: {
    arousal: PersonalityScore; // High/low energy in voice
    valence: PersonalityScore; // Positive/negative emotion
    dominance: PersonalityScore; // Confident/submissive tone
    stress: PersonalityScore; // Vocal stress indicators
    excitement: PersonalityScore; // Enthusiasm level
    uncertainty: PersonalityScore; // Hesitation/confidence
  };
  
  /** Personality-relevant voice features */
  personalityVoiceMarkers: {
    extraversion: {
      loudness: PersonalityScore;
      speechRate: PersonalityScore;
      tonalVariation: PersonalityScore;
      pauseFrequency: PersonalityScore;
    };
    conscientiousness: {
      articulationClarity: PersonalityScore;
      speechPlanning: PersonalityScore;
      consistencyMeasures: PersonalityScore;
    };
    openness: {
      prosodyVariation: PersonalityScore;
      creativeExpression: PersonalityScore;
      linguisticComplexity: PersonalityScore;
    };
    agreeableness: {
      warmthIndicators: PersonalityScore;
      cooperativeVocalCues: PersonalityScore;
      empathyVoiceMarkers: PersonalityScore;
    };
    neuroticism: {
      voiceStability: PersonalityScore;
      anxietyMarkers: PersonalityScore;
      stressCues: PersonalityScore;
    };
  };
  
  /** Processing metadata */
  processingMetadata: {
    sampleRate: number;
    duration: number; // seconds
    qualityScore: number; // 0-1 audio quality
    noiseLevel: number; // 0-1 background noise
    processingLatency: number; // milliseconds
    confidenceScore: number; // 0-1 overall analysis confidence
  };
}

/**
 * Cross-modal personality correlation
 */
export interface CrossModalPersonalityCorrelation {
  /** Voice-text alignment */
  voiceTextAlignment: {
    emotionalConsistency: PersonalityScore;
    energyLevelAlignment: PersonalityScore;
    formalityConsistency: PersonalityScore;
    confidenceAlignment: PersonalityScore;
    overallCoherence: PersonalityScore;
  };
  
  /** Behavioral pattern consistency */
  behavioralConsistency: {
    responsePatternAlignment: PersonalityScore;
    engagementConsistency: PersonalityScore;
    topicHandlingConsistency: PersonalityScore;
    socialStyleAlignment: PersonalityScore;
  };
  
  /** Multi-modal confidence */
  multiModalConfidence: {
    voiceConfidence: number; // 0-1 confidence in voice analysis
    textConfidence: number; // 0-1 confidence in text analysis
    behavioralConfidence: number; // 0-1 confidence in behavioral analysis
    overallConfidence: number; // 0-1 combined confidence
    uncertaintyFactors: string[]; // Sources of uncertainty
  };
  
  /** Conflict resolution */
  conflictResolution: Array<{
    trait: string;
    voiceIndication: PersonalityScore;
    textIndication: PersonalityScore;
    behavioralIndication: PersonalityScore;
    resolvedValue: PersonalityScore;
    resolutionStrategy: 'weighted_average' | 'confidence_based' | 'temporal_priority' | 'context_dependent';
    resolutionConfidence: number;
  }>;
}

/**
 * Validation framework for multimodal personality analysis
 */
export interface MultiModalValidation {
  /** Cross-modal consistency checks */
  consistencyChecks: {
    voiceTextConsistency: ValidationResult;
    behavioralPatternConsistency: ValidationResult;
    temporalConsistency: ValidationResult;
    contextualConsistency: ValidationResult;
  };
  
  /** Quality assurance metrics */
  qualityMetrics: {
    dataCompleteness: ValidationResult;
    signalQuality: ValidationResult;
    processingAccuracy: ValidationResult;
    confidenceCalibration: ValidationResult;
  };
  
  /** Reliability indicators */
  reliabilityIndicators: {
    testRetestReliability: number; // 0-1 consistency over time
    interModalReliability: number; // 0-1 agreement between modalities
    contextualStability: number; // 0-1 stability across contexts
    predictionAccuracy: number; // 0-1 accuracy against known benchmarks
  };
  
  /** Validation flags */
  validationFlags: {
    insufficientData: boolean;
    conflictingSignals: boolean;
    lowConfidence: boolean;
    contextualAnomalies: boolean;
    processingErrors: boolean;
    privacyConstraints: boolean;
  };
}

export interface ValidationResult {
  passed: boolean;
  score: number; // 0-1 validation score
  threshold: number; // Required threshold for passing
  details: string; // Human-readable validation details
  recommendations: string[]; // Recommendations for improvement
  timestamp: Date;
}

// =============================================================================
// REAL-TIME STREAMING TYPES
// =============================================================================

/**
 * Real-time personality streaming event types
 */
export enum PersonalityStreamEventType {
  TRAIT_UPDATE = 'trait_update',
  CONFIDENCE_CHANGE = 'confidence_change',
  PATTERN_DETECTED = 'pattern_detected',
  ADAPTATION_TRIGGERED = 'adaptation_triggered',
  VALIDATION_COMPLETED = 'validation_completed',
  ERROR_OCCURRED = 'error_occurred',
  PROCESSING_STARTED = 'processing_started',
  PROCESSING_COMPLETED = 'processing_completed'
}

/**
 * Personality streaming event
 */
export interface PersonalityStreamEvent {
  eventType: PersonalityStreamEventType;
  timestamp: Date;
  roomId: string;
  speakerId: string;
  eventId: string;
  data: PersonalityStreamEventData;
  metadata: {
    processingLatency: number; // milliseconds
    dataSource: 'voice' | 'text' | 'behavioral' | 'multimodal';
    confidenceLevel: number; // 0-1
    privacyLevel: 'public' | 'private' | 'anonymized';
  };
}

export type PersonalityStreamEventData = 
  | TraitUpdateData
  | ConfidenceChangeData
  | PatternDetectedData
  | AdaptationTriggeredData
  | ValidationCompletedData
  | ErrorOccurredData
  | ProcessingStatusData;

export interface TraitUpdateData {
  trait: string;
  previousValue: PersonalityScore;
  newValue: PersonalityScore;
  changeSignificance: number; // 0-1 how significant this change is
  triggerSource: string; // What caused this update
  propagationNeeded: boolean; // Whether companion behavior should adapt
}

export interface ConfidenceChangeData {
  trait: string;
  previousConfidence: number;
  newConfidence: number;
  confidenceFactors: Array<{
    factor: string;
    impact: number; // -1 to 1 impact on confidence
    explanation: string;
  }>;
}

export interface PatternDetectedData {
  patternType: string;
  patternStrength: number; // 0-1 how strong the pattern is
  patternDescription: string;
  personalityImplications: Array<{
    trait: string;
    implications: string;
    confidenceImpact: number;
  }>;
}

export interface AdaptationTriggeredData {
  adaptationType: 'style' | 'behavior' | 'parameter';
  adaptationTarget: string;
  previousValue: any;
  newValue: any;
  adaptationReason: string;
  expectedImpact: string;
}

export interface ValidationCompletedData {
  validationType: string;
  validationResult: ValidationResult;
  impactedTraits: string[];
}

export interface ErrorOccurredData {
  errorType: string;
  errorMessage: string;
  errorCode: string;
  affectedSystems: string[];
  recoveryAction?: string;
}

export interface ProcessingStatusData {
  stage: string;
  progress: number; // 0-1 completion percentage
  estimatedTimeRemaining: number; // milliseconds
  currentOperation: string;
}

/**
 * Real-time personality broadcast configuration
 */
export interface PersonalityBroadcastConfig {
  /** Event filtering */
  eventFilters: {
    significanceThreshold: number; // 0-1 minimum change significance to broadcast
    confidenceThreshold: number; // 0-1 minimum confidence to broadcast
    updateFrequencyLimit: number; // Maximum updates per second
    eventTypes: PersonalityStreamEventType[]; // Which event types to broadcast
  };
  
  /** Privacy settings */
  privacySettings: {
    anonymizeData: boolean;
    excludeSensitiveTraits: string[];
    consentRequired: boolean;
    dataRetentionPeriod: number; // Hours to retain broadcast data
  };
  
  /** Performance settings */
  performanceSettings: {
    batchingEnabled: boolean;
    batchSize: number;
    batchTimeout: number; // Milliseconds
    compressionEnabled: boolean;
    priorityQueueEnabled: boolean;
  };
}

// =============================================================================
// ADAPTIVE COMPANION BEHAVIOR TYPES
// =============================================================================

/**
 * Adaptive companion behavior based on personality inference
 */
export interface AdaptiveCompanionBehavior {
  /** Behavior adaptation rules */
  adaptationRules: Array<{
    triggerId: string;
    condition: PersonalityCondition;
    adaptations: BehaviorAdaptation[];
    priority: number; // 1-10 priority for conflicting rules
    cooldownPeriod: number; // Seconds before rule can trigger again
    maxAdaptations: number; // Maximum adaptations per session
  }>;
  
  /** Current adaptations */
  currentAdaptations: Map<string, {
    adaptation: BehaviorAdaptation;
    appliedAt: Date;
    confidence: number;
    performance: number; // 0-1 how well this adaptation is working
    userFeedback?: 'positive' | 'negative' | 'neutral';
  }>;
  
  /** Adaptation history */
  adaptationHistory: Array<{
    timestamp: Date;
    adaptation: BehaviorAdaptation;
    trigger: string;
    outcome: 'successful' | 'failed' | 'neutral';
    userEngagement: number; // 0-1 user engagement after adaptation
    revertedAt?: Date;
    revertReason?: string;
  }>;
}

export interface PersonalityCondition {
  trait: string;
  operator: 'gt' | 'lt' | 'eq' | 'gte' | 'lte' | 'range';
  value: number | [number, number];
  confidence: number; // Minimum confidence required
  duration?: number; // Seconds the condition must be true
  stability?: number; // Required trait stability
}

export interface BehaviorAdaptation {
  adaptationType: 'response_style' | 'conversation_approach' | 'content_strategy' | 'interaction_pattern';
  adaptationId: string;
  parameters: Record<string, any>;
  description: string;
  expectedOutcome: string;
  reversible: boolean;
  impactLevel: 'subtle' | 'moderate' | 'significant';
}

/**
 * Companion behavior modification events
 */
export interface CompanionBehaviorEvent {
  eventType: 'adaptation_applied' | 'adaptation_reverted' | 'behavior_evaluated' | 'user_feedback_received';
  timestamp: Date;
  companionId: string;
  roomId: string;
  adaptationId: string;
  details: {
    previousBehavior?: any;
    newBehavior?: any;
    userResponse?: string;
    performanceMetrics?: {
      engagementChange: number; // -1 to 1 change in engagement
      satisfactionChange: number; // -1 to 1 change in satisfaction
      conversationFlow: number; // 0-1 conversation quality
    };
  };
}

// =============================================================================
// AI PROVIDER INTEGRATION TYPES
// =============================================================================

/**
 * Multi-provider AI integration for personality inference
 */
export interface PersonalityInferenceProvider {
  providerId: string;
  providerName: string;
  capabilities: {
    zeroShotInference: boolean;
    multiModalAnalysis: boolean;
    realTimeProcessing: boolean;
    voiceAnalysis: boolean;
    behavioralAnalysis: boolean;
    adaptiveLearning: boolean;
  };
  
  /** Provider-specific configuration */
  configuration: {
    modelVersion: string;
    apiEndpoint: string;
    rateLimits: {
      requestsPerMinute: number;
      requestsPerHour: number;
      requestsPerDay: number;
    };
    costPerRequest: number;
    averageLatency: number; // milliseconds
    reliability: number; // 0-1 uptime percentage
  };
  
  /** Provider performance metrics */
  performance: {
    accuracy: number; // 0-1 accuracy on validation set
    consistency: number; // 0-1 consistency over time
    speed: number; // requests per second
    errorRate: number; // 0-1 error rate
    userSatisfaction: number; // 0-1 user satisfaction with results
  };
}

/**
 * Intelligent provider selection
 */
export interface ProviderSelectionStrategy {
  /** Selection criteria */
  selectionCriteria: {
    priorityFactors: Array<{
      factor: 'accuracy' | 'speed' | 'cost' | 'reliability' | 'capabilities';
      weight: number; // 0-1 importance weight
      threshold?: number; // Minimum acceptable value
    }>;
    fallbackChain: string[]; // Provider IDs in fallback order
    loadBalancing: 'round_robin' | 'weighted' | 'performance_based' | 'cost_optimized';
  };
  
  /** Dynamic selection logic */
  dynamicSelection: {
    enabled: boolean;
    evaluationInterval: number; // Seconds between provider evaluations
    switchThreshold: number; // Performance difference to trigger switch
    minimumStickyPeriod: number; // Seconds to stick with a provider
    adaptToWorkload: boolean; // Adapt selection based on current load
  };
  
  /** Provider health monitoring */
  healthMonitoring: {
    healthCheckInterval: number; // Seconds between health checks
    timeoutThreshold: number; // Milliseconds before timeout
    errorThreshold: number; // Error rate threshold for unhealthy status
    recoveryPeriod: number; // Seconds before retrying unhealthy provider
  };
}

/**
 * Provider response standardization
 */
export interface StandardizedPersonalityResponse {
  /** Standardized response format */
  personalityInference: ZeroShotPersonalityInference;
  confidence: {
    overall: number; // 0-1 overall confidence
    perTrait: Record<string, number>; // 0-1 confidence per trait
    calibrated: boolean; // Whether confidence is calibrated
  };
  
  /** Provider-specific metadata */
  providerMetadata: {
    providerId: string;
    modelVersion: string;
    processingTime: number; // milliseconds
    inputTokens: number;
    outputTokens: number;
    cost: number; // USD cost for this request
  };
  
  /** Quality indicators */
  qualityIndicators: {
    dataQuality: 'high' | 'medium' | 'low';
    consistencyScore: number; // 0-1 consistency with previous inferences
    noveltyScore: number; // 0-1 how much new information was detected
    uncertaintyLevel: number; // 0-1 uncertainty in the inference
  };
  
  /** Validation results */
  validation: MultiModalValidation;
}

// =============================================================================
// PRIVACY AND COMPLIANCE TYPES
// =============================================================================

/**
 * Comprehensive consent management for personality data
 */
export interface PersonalityDataConsent {
  /** Consent status */
  consentStatus: {
    given: boolean;
    timestamp: Date;
    version: string; // Consent policy version
    method: 'explicit' | 'implicit' | 'opt_out';
    ipAddress: string;
    userAgent: string;
  };
  
  /** Granular permissions */
  permissions: {
    voiceAnalysis: ConsentLevel;
    behavioralAnalysis: ConsentLevel;
    personalityInference: ConsentLevel;
    dataStorage: ConsentLevel;
    dataSharing: ConsentLevel;
    adaptiveBehavior: ConsentLevel;
    analyticsUsage: ConsentLevel;
  };
  
  /** Data processing preferences */
  processingPreferences: {
    onDeviceProcessing: boolean; // Prefer on-device processing when possible
    anonymization: boolean; // Anonymize data before processing
    dataMinimization: boolean; // Use minimum necessary data
    purposeLimitation: boolean; // Limit data use to stated purposes
    retentionPeriod: number; // Days to retain data (0 = delete immediately)
  };
  
  /** User rights management */
  userRights: {
    accessRequest: boolean; // Right to access personal data
    rectification: boolean; // Right to correct inaccurate data
    erasure: boolean; // Right to be forgotten
    portability: boolean; // Right to data portability
    restriction: boolean; // Right to restrict processing
    objection: boolean; // Right to object to processing
  };
}

export enum ConsentLevel {
  DENIED = 'denied',
  BASIC = 'basic', // Minimal processing only
  STANDARD = 'standard', // Standard features enabled
  ENHANCED = 'enhanced', // All features enabled
  RESEARCH = 'research' // Additional research use allowed
}

/**
 * Privacy-preserving data structures
 */
export interface PrivacyPreservingPersonalityData {
  /** Anonymized identifiers */
  anonymizedIds: {
    sessionId: string; // Session-specific anonymous ID
    deviceFingerprint: string; // Anonymous device identifier
    cohortId: string; // Anonymous cohort for similar users
    temporalId: string; // Time-based anonymous identifier
  };
  
  /** Differential privacy */
  differentialPrivacy: {
    enabled: boolean;
    epsilonValue: number; // Privacy budget (lower = more private)
    deltaValue: number; // Failure probability
    noiseInjection: boolean; // Whether noise has been injected
    privacyBudgetRemaining: number; // Remaining privacy budget
  };
  
  /** Data minimization */
  dataMinimization: {
    originalDataSize: number; // Original data size in bytes
    minimizedDataSize: number; // Minimized data size in bytes
    reductionRatio: number; // Data reduction ratio
    retainedFeatures: string[]; // Features retained after minimization
    approximationError: number; // Error introduced by minimization
  };
  
  /** Anonymization techniques */
  anonymization: {
    techniques: Array<'k_anonymity' | 'l_diversity' | 't_closeness' | 'generalization' | 'suppression'>;
    anonymityLevel: number; // K-anonymity level achieved
    informationLoss: number; // 0-1 information loss from anonymization
    reidentificationRisk: number; // 0-1 risk of re-identification
  };
}

/**
 * Comprehensive data lifecycle management
 */
export interface PersonalityDataLifecycle {
  /** Data collection phase */
  collection: {
    timestamp: Date;
    source: 'voice' | 'text' | 'behavioral' | 'contextual';
    consentVersion: string;
    legalBasis: 'consent' | 'legitimate_interest' | 'contract' | 'legal_obligation';
    purpose: string[]; // Purposes for data collection
    retentionPeriod: number; // Days to retain data
  };
  
  /** Data processing phase */
  processing: {
    processedAt: Date;
    processingPurpose: string[];
    anonymizationApplied: boolean;
    encryptionApplied: boolean;
    accessLog: Array<{
      timestamp: Date;
      accessor: string;
      purpose: string;
      approved: boolean;
    }>;
  };
  
  /** Data storage phase */
  storage: {
    storageLocation: 'local' | 'cloud' | 'edge';
    encryption: {
      algorithm: string;
      keyRotation: Date;
      keyDerivation: string;
    };
    backupStatus: boolean;
    compressionApplied: boolean;
    checksumVerification: string;
  };
  
  /** Data sharing phase */
  sharing: {
    sharingEvents: Array<{
      timestamp: Date;
      recipient: string;
      purpose: string;
      dataCategories: string[];
      consentVerified: boolean;
      safeguardsApplied: string[];
    }>;
    thirdPartyProcessors: Array<{
      processorName: string;
      contractDate: Date;
      safeguardLevel: string;
      auditDate?: Date;
    }>;
  };
  
  /** Data deletion phase */
  deletion: {
    scheduledDeletion: Date;
    deletionTriggers: Array<'retention_period' | 'consent_withdrawal' | 'user_request' | 'legal_requirement'>;
    deletionStatus: 'pending' | 'in_progress' | 'completed' | 'failed';
    secureWiping: boolean;
    deletionCertificate?: string;
    backupDeletion: boolean;
  };
}

// =============================================================================
// PERFORMANCE OPTIMIZATION TYPES
// =============================================================================

/**
 * High-performance caching for personality data
 */
export interface PersonalityCacheStrategy {
  /** Cache configuration */
  cacheConfig: {
    enabled: boolean;
    maxMemoryUsage: number; // MB
    maxDiskUsage: number; // MB
    compressionEnabled: boolean;
    encryptionEnabled: boolean;
    evictionPolicy: 'lru' | 'lfu' | 'ttl' | 'confidence_based' | 'custom';
  };
  
  /** Cache levels */
  cacheLevels: {
    l1Cache: { // In-memory cache
      maxSize: number; // Number of entries
      ttl: number; // Seconds
      hitRatio: number; // 0-1 current hit ratio
    };
    l2Cache: { // Redis cache
      maxSize: number;
      ttl: number;
      hitRatio: number;
      clusterEnabled: boolean;
    };
    l3Cache: { // Persistent storage cache
      maxSize: number;
      ttl: number;
      hitRatio: number;
      compressionRatio: number;
    };
  };
  
  /** Confidence-based caching */
  confidenceBasedCaching: {
    enabled: boolean;
    highConfidenceThreshold: number; // 0-1 threshold for high confidence caching
    highConfidenceTTL: number; // Seconds to cache high confidence data
    lowConfidenceThreshold: number; // 0-1 threshold for low confidence caching
    lowConfidenceTTL: number; // Seconds to cache low confidence data
    confidenceDecayRate: number; // Rate at which confidence decays over time
  };
  
  /** Intelligent prefetching */
  prefetching: {
    enabled: boolean;
    prefetchDepth: number; // How many related items to prefetch
    prefetchThreshold: number; // Access frequency threshold for prefetch
    predictionAccuracy: number; // 0-1 accuracy of prefetch predictions
    prefetchHitRatio: number; // 0-1 hit ratio for prefetched items
  };
}

/**
 * Performance monitoring for personality inference
 */
export interface PersonalityPerformanceMetrics {
  /** Latency metrics */
  latencyMetrics: {
    p50: number; // 50th percentile latency (ms)
    p95: number; // 95th percentile latency (ms)
    p99: number; // 99th percentile latency (ms)
    mean: number; // Mean latency (ms)
    max: number; // Maximum latency (ms)
    trend: 'improving' | 'stable' | 'degrading';
  };
  
  /** Throughput metrics */
  throughputMetrics: {
    requestsPerSecond: number;
    concurrentUsers: number;
    queueLength: number;
    processingCapacity: number; // Maximum sustainable requests per second
    utilizationRate: number; // 0-1 current utilization
  };
  
  /** Quality metrics */
  qualityMetrics: {
    accuracyRate: number; // 0-1 accuracy against validation set
    consistencyRate: number; // 0-1 consistency across repeated inference
    errorRate: number; // 0-1 error rate
    timeoutRate: number; // 0-1 timeout rate
    userSatisfactionScore: number; // 0-1 user satisfaction
  };
  
  /** Resource utilization */
  resourceUtilization: {
    cpuUsage: number; // 0-1 CPU utilization
    memoryUsage: number; // 0-1 memory utilization
    networkUsage: number; // 0-1 network utilization
    diskUsage: number; // 0-1 disk utilization
    gpuUsage?: number; // 0-1 GPU utilization if available
  };
}

// =============================================================================
// ZOOD SCHEMAS FOR VALIDATION
// =============================================================================

export const PersonalityScoreSchema = z.object({
  value: z.number().min(0).max(100),
  confidence: z.number().min(0).max(1),
  evidence: z.array(z.string()),
  uncertainty: z.number().min(0).max(1),
  temporalStability: z.number().min(0).max(1),
  sources: z.array(z.object({
    type: z.enum(['voice', 'text', 'behavioral', 'contextual']),
    weight: z.number().min(0).max(1),
    timestamp: z.date()
  }))
});

export const ZeroShotPersonalityInferenceSchema = z.object({
  oceanTraits: z.object({
    openness: PersonalityScoreSchema,
    conscientiousness: PersonalityScoreSchema,
    extraversion: PersonalityScoreSchema,
    agreeableness: PersonalityScoreSchema,
    neuroticism: PersonalityScoreSchema
  }),
  communicationPatterns: z.object({
    formality: PersonalityScoreSchema,
    verbosity: PersonalityScoreSchema,
    emotionalExpression: PersonalityScoreSchema,
    technicalLanguage: PersonalityScoreSchema,
    humor: PersonalityScoreSchema,
    empathy: PersonalityScoreSchema
  }),
  cognitiveStyle: z.object({
    analyticalThinking: PersonalityScoreSchema,
    creativityLevel: PersonalityScoreSchema,
    detailOrientation: PersonalityScoreSchema,
    abstractThinking: PersonalityScoreSchema,
    problemSolvingApproach: PersonalityScoreSchema
  }),
  socialStyle: z.object({
    collaborationPreference: PersonalityScoreSchema,
    leadershipTendency: PersonalityScoreSchema,
    conflictResolution: PersonalityScoreSchema,
    supportiveness: PersonalityScoreSchema,
    assertiveness: PersonalityScoreSchema
  }),
  motivationalProfile: z.object({
    achievementOrientation: PersonalityScoreSchema,
    curiosityLevel: PersonalityScoreSchema,
    persistenceLevel: PersonalityScoreSchema,
    feedbackReceptivity: PersonalityScoreSchema,
    riskTolerance: PersonalityScoreSchema
  })
});

export const PersonalityStreamEventSchema = z.object({
  eventType: z.nativeEnum(PersonalityStreamEventType),
  timestamp: z.date(),
  roomId: z.string().uuid(),
  speakerId: z.string(),
  eventId: z.string().uuid(),
  data: z.any(), // Union type too complex for Zod
  metadata: z.object({
    processingLatency: z.number().positive(),
    dataSource: z.enum(['voice', 'text', 'behavioral', 'multimodal']),
    confidenceLevel: z.number().min(0).max(1),
    privacyLevel: z.enum(['public', 'private', 'anonymized'])
  })
});

export const PersonalityDataConsentSchema = z.object({
  consentStatus: z.object({
    given: z.boolean(),
    timestamp: z.date(),
    version: z.string(),
    method: z.enum(['explicit', 'implicit', 'opt_out']),
    ipAddress: z.string(),
    userAgent: z.string()
  }),
  permissions: z.object({
    voiceAnalysis: z.nativeEnum(ConsentLevel),
    behavioralAnalysis: z.nativeEnum(ConsentLevel),
    personalityInference: z.nativeEnum(ConsentLevel),
    dataStorage: z.nativeEnum(ConsentLevel),
    dataSharing: z.nativeEnum(ConsentLevel),
    adaptiveBehavior: z.nativeEnum(ConsentLevel),
    analyticsUsage: z.nativeEnum(ConsentLevel)
  }),
  processingPreferences: z.object({
    onDeviceProcessing: z.boolean(),
    anonymization: z.boolean(),
    dataMinimization: z.boolean(),
    purposeLimitation: z.boolean(),
    retentionPeriod: z.number().min(0)
  }),
  userRights: z.object({
    accessRequest: z.boolean(),
    rectification: z.boolean(),
    erasure: z.boolean(),
    portability: z.boolean(),
    restriction: z.boolean(),
    objection: z.boolean()
  })
});

// All types are exported via 'export interface' declarations above
// No need for additional export declarations